import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import AppHeader from '../components/AppHeader';
import FeatureGate from '../components/FeatureGate';
import {
  Code2, Play, Rocket, Terminal, ExternalLink, Copy, Check,
  Loader2, ArrowLeft, Trash2, RefreshCw, AlertCircle, Globe, Sparkles,
  ChevronDown, Brain, Cpu, History, Plus, MessageSquare, Send,
  PanelLeftClose, PanelLeft,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PREVIEW_URL = `${API}/autonomous-engineer/preview/`;

const NGROK_HEADERS = { 'ngrok-skip-browser-warning': 'true' };

const MODEL_OPTIONS = {
  anthropic: [
    { id: 'claude-sonnet-4-6', label: 'Claude 4.6 Sonnet' },
    { id: 'claude-haiku-4-5-20251001', label: 'Claude 4.5 Haiku' },
  ],
  openai: [
    { id: 'gpt-5.1', label: 'GPT-5.1 (Coding)' },
    { id: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
    { id: 'o3', label: 'O3 (Reasoning)' },
    { id: 'o4-mini', label: 'O4 Mini (Reasoning)' },
    { id: 'o3-pro', label: 'O3 Pro' },
  ],
};
const STAGES = [
  { key: 'planning', label: 'Planning', icon: Brain },
  { key: 'generating', label: 'Generating', icon: Sparkles },
  { key: 'writing', label: 'Writing Files', icon: Code2 },
  { key: 'container', label: 'Docker Container', icon: Terminal },
  { key: 'installing', label: 'Installing', icon: Loader2 },
  { key: 'starting', label: 'Starting Server', icon: Play },
  { key: 'testing', label: 'Smoke Test', icon: Globe },
];

function card(isDark) {
  return {
    background: isDark ? '#0f1729' : '#ffffff',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'}`,
    boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.4)' : '0 1px 6px rgba(0,0,0,0.04)',
  };
}

export default function CodeEngineer() {
  return (
    <FeatureGate feature="autonomous_engineer" featureLabel="Autonomous Software Engineer">
      <CodeEngineerInner />
    </FeatureGate>
  );
}

function CodeEngineerInner() {
  const { isDark } = useTheme();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [prompt, setPrompt] = useState('');
  const [provider, setProvider] = useState('anthropic');
  const [model, setModel] = useState('claude-opus-4-6');
  const [sessionId, setSessionId] = useState(null);
  const [status, setStatus] = useState('idle'); // idle|building|ready|deploying|deployed|error
  const [currentStage, setCurrentStage] = useState(null);
  const [completedStages, setCompletedStages] = useState(new Set());
  const [logs, setLogs] = useState([]);
  const [tunnelUrl, setTunnelUrl] = useState(null);
  const [vercelUrl, setVercelUrl] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null); // 'tunnel' | 'vercel'
  const [iframeKey, setIframeKey] = useState(0);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Session history state
  const [sessions, setSessions] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const [editPrompt, setEditPrompt] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const logsEndRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // Close model dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setModelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch session history on mount
  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/autonomous-engineer/sessions`, { headers: NGROK_HEADERS });
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
      }
    } catch {}
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // Fetch messages when session changes
  const fetchMessages = useCallback(async (sid) => {
    if (!sid) { setMessages([]); return; }
    try {
      const res = await fetch(`${API}/autonomous-engineer/session/${sid}/messages`, { headers: NGROK_HEADERS });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch {}
  }, []);

  const addLog = useCallback((text) => {
    setLogs((prev) => [...prev.slice(-499), { text, ts: Date.now() }]);
  }, []);

  // Load a session from history — always calls /restore to swap files and restart preview
  const loadSession = useCallback(async (s) => {
    setSessionId(s.id);
    setPrompt(s.prompt || '');
    setError(null);
    setLogs([]);
    setCurrentStage(null);
    setCompletedStages(new Set());
    setTunnelUrl(null);
    fetchMessages(s.id);

    // Always restore: load files from DB, write to preview dir, restart dev server
    setStatus('building');
    setCurrentStage('restoring');
    addLog('Restoring session preview...');

    try {
      const res = await fetch(`${API}/autonomous-engineer/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...NGROK_HEADERS },
        body: JSON.stringify({ sessionId: s.id }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = null;
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6));
              if (eventType === 'status') {
                setCurrentStage(data.stage);
                if (data.stage === 'ready') {
                  setStatus('ready');
                  setTunnelUrl(PREVIEW_URL);
                  setIframeKey((k) => k + 1);
                }
              } else if (eventType === 'log') {
                addLog(data.text);
              } else if (eventType === 'error') {
                setError(data.message);
                setStatus('error');
              } else if (eventType === 'complete') {
                setStatus('ready');
                setTunnelUrl(PREVIEW_URL);
                setIframeKey((k) => k + 1);
                fetchSessions();
              }
            } catch {}
            eventType = null;
          }
        }
      }
    } catch (err) {
      setError(`Restore failed: ${err.message}`);
      setStatus('error');
    }
  }, [fetchMessages, fetchSessions, addLog]);

  const handleNewProject = useCallback(() => {
    setSessionId(null);
    setStatus('idle');
    setPrompt('');
    setError(null);
    setLogs([]);
    setTunnelUrl(null);
    setVercelUrl(null);
    setCurrentStage(null);
    setCompletedStages(new Set());
    setMessages([]);
    setEditPrompt('');
  }, []);

  const handleBuild = async () => {
    if (!prompt.trim()) return;
    setStatus('building');
    setError(null);
    setLogs([]);
    setTunnelUrl(null);
    setVercelUrl(null);
    setSessionId(null);
    setCurrentStage('generating');
    setCompletedStages(new Set());

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API}/autonomous-engineer/build`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...NGROK_HEADERS },
        body: JSON.stringify({ prompt: prompt.trim(), provider, model }),
        signal: controller.signal,
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = null;
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              handleSSE(currentEvent, data);
            } catch {}
            currentEvent = null;
          } else if (line.trim() === '') {
            currentEvent = null;
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
        setStatus('error');
      }
    }
    fetchSessions();
  };

  const handleSSE = useCallback((eventType, data) => {
    // error event — always handle first
    if (eventType === 'error' || data.error) {
      const msg = data.error || data.message || 'Unknown error';
      setError(msg);
      setStatus('error');
      // Keep currentStage so the pipeline shows which stage failed (red highlight)
      addLog(`✗ ERROR: ${msg}`);
      return;
    }

    // session event — refresh sidebar immediately so new session appears
    if (data.sessionId && !data.tunnelUrl) {
      setSessionId(data.sessionId);
      fetchSessions();
    }

    // status events
    if (data.stage) {
      const stageKey = data.stage;
      setCurrentStage(stageKey);
      addLog(`[${stageKey.toUpperCase()}] ${data.message || ''}`);

      // Mark previous stages as completed
      const stageKeys = STAGES.map((s) => s.key);
      const idx = stageKeys.indexOf(stageKey);
      if (idx > 0) {
        setCompletedStages((prev) => {
          const next = new Set(prev);
          for (let i = 0; i < idx; i++) next.add(stageKeys[i]);
          return next;
        });
      }

      // Terminal stages
      if (stageKey === 'ready') {
        setCompletedStages((prev) => {
          const next = new Set(prev);
          STAGES.forEach((s) => next.add(s.key));
          return next;
        });
      }
    }

    // log event
    if (data.text && !data.stage) {
      addLog(data.text);
    }

    // complete event
    if (data.tunnelUrl) {
      setTunnelUrl(PREVIEW_URL);
      setSessionId(data.sessionId || sessionId);
      setStatus('ready');
      setCurrentStage(null);
      setCompletedStages(new Set(STAGES.map((s) => s.key)));
      addLog(`✓ Preview ready: ${previewUrl}`);
      fetchSessions();
    }
  }, [addLog, sessionId, fetchSessions]);

  const handleDeploy = async () => {
    if (!sessionId) return;
    setStatus('deploying');
    setError(null);
    addLog('[DEPLOY] Deploying to Vercel...');
    try {
      const res = await fetch(`${API}/autonomous-engineer/deploy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...NGROK_HEADERS },
        body: JSON.stringify({ sessionId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Deploy failed');
      setVercelUrl(data.vercelUrl);
      setStatus('deployed');
      addLog(`✓ Deployed: ${data.vercelUrl}`);
    } catch (err) {
      setError(err.message);
      setStatus('error');
      addLog(`✗ Deploy failed: ${err.message}`);
    }
  };

  const handleCleanup = async () => {
    if (!sessionId) return;
    try {
      await fetch(`${API}/autonomous-engineer/cleanup/${sessionId}`, { method: 'POST', headers: NGROK_HEADERS });
      setStatus('idle');
      setSessionId(null);
      setTunnelUrl(null);
      setVercelUrl(null);
      setLogs([]);
      setCurrentStage(null);
      setCompletedStages(new Set());
      setError(null);
      setMessages([]);
      addLog('[CLEANUP] Session cleaned up');
      fetchSessions();
    } catch {}
  };

  // Handle iterative edit
  const handleEdit = async () => {
    if (!editPrompt.trim() || !sessionId) return;
    setIsEditing(true);
    setError(null);

    // Optimistically add user message
    const userMsg = { role: 'user', content: editPrompt.trim(), createdAt: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    const currentEditPrompt = editPrompt;
    setEditPrompt('');

    try {
      const res = await fetch(`${API}/autonomous-engineer/edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...NGROK_HEADERS },
        body: JSON.stringify({ sessionId, prompt: currentEditPrompt, provider, model }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = null;
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              // Handle edit SSE events
              if (currentEvent === 'error' || data.error) {
                setError(data.error || data.message || 'Edit failed');
              } else if (data.text && !data.stage) {
                addLog(data.text);
              } else if (data.stage) {
                addLog(`[${data.stage.toUpperCase()}] ${data.message || ''}`);
              }
              if (data.tunnelUrl) {
                setTunnelUrl(PREVIEW_URL);
                setIframeKey((k) => k + 1);
              }
              if (currentEvent === 'complete') {
                // Refresh preview
                setIframeKey((k) => k + 1);
              }
            } catch {}
            currentEvent = null;
          }
        }
      }

      // Refresh messages from server
      await fetchMessages(sessionId);
    } catch (err) {
      setError(err.message);
      addLog(`✗ Edit failed: ${err.message}`);
    }
    setIsEditing(false);
  };

  const copyUrl = (url, type) => {
    navigator.clipboard.writeText(url);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const accent = '#F87217';
  const bg = isDark ? '#060b18' : '#f8fafc';
  const textPrimary = isDark ? '#e2e8f0' : '#1e293b';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';

  const isBuilding = status === 'building';
  const isError = status === 'error';
  const isReady = status === 'ready' || status === 'deployed' || status === 'deploying';

  return (
    <div style={{ minHeight: '100vh', background: bg }}>
      <AppHeader />
      <div style={{ display: 'flex', maxWidth: 1800, margin: '0 auto' }}>

        {/* ── Session History Sidebar ── */}
        <div
          style={{
            width: sidebarOpen ? 260 : 0,
            minWidth: sidebarOpen ? 260 : 0,
            overflow: 'hidden',
            transition: 'all 0.2s',
            borderRight: sidebarOpen ? `1px solid ${borderColor}` : 'none',
            background: isDark ? '#0a0f1e' : '#f9fafb',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {sidebarOpen && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 14px 12px' }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={handleNewProject}
                    style={{
                      background: accent,
                      border: 'none',
                      borderRadius: 6,
                      padding: '4px 10px',
                      cursor: 'pointer',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Plus size={12} /> New
                  </button>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: textSecondary }}
                  >
                    <PanelLeftClose size={16} />
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <History size={14} color={textSecondary} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: textPrimary }}>Sessions</span>
                </div>
              </div>
              <div style={{ flex: 1, overflow: 'auto', padding: '0 8px 12px' }}>
                {sessions.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '24px 12px', color: textSecondary, fontSize: 12 }}>
                    No sessions yet. Build your first project!
                  </div>
                )}
                {sessions.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => loadSession(s)}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      marginBottom: 4,
                      cursor: 'pointer',
                      background: sessionId === s.id
                        ? isDark ? `${accent}18` : `${accent}10`
                        : 'transparent',
                      border: sessionId === s.id ? `1px solid ${accent}40` : '1px solid transparent',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (sessionId !== s.id) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : '#f1f5f9';
                    }}
                    onMouseLeave={(e) => {
                      if (sessionId !== s.id) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <div style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: textPrimary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginBottom: 4,
                    }}>
                      {s.projectName || 'Untitled Project'}
                    </div>
                    <div style={{
                      fontSize: 11,
                      color: textSecondary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginBottom: 4,
                    }}>
                      {s.prompt}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '1px 6px',
                          borderRadius: 4,
                          background: s.status === 'ready' ? 'rgba(34,197,94,0.15)' : s.status === 'error' ? 'rgba(239,68,68,0.15)' : isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0',
                          color: s.status === 'ready' ? '#22c55e' : s.status === 'error' ? '#ef4444' : textSecondary,
                        }}
                      >
                        {s.status}
                      </span>
                      <span style={{ fontSize: 10, color: textSecondary }}>
                        {new Date(s.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── Main Content ── */}
        <div style={{ flex: 1, padding: '24px 24px 48px', minWidth: 0 }}>

        {/* Sidebar toggle when closed */}
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            style={{
              position: 'fixed',
              left: 8,
              top: 80,
              background: isDark ? '#1a2236' : '#ffffff',
              border: `1px solid ${borderColor}`,
              borderRadius: 8,
              padding: '6px',
              cursor: 'pointer',
              color: textSecondary,
              zIndex: 40,
              boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.4)' : '0 1px 6px rgba(0,0,0,0.1)',
            }}
          >
            <PanelLeft size={16} />
          </button>
        )}
        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button
            onClick={() => navigate('/marketplace')}
            style={{
              background: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
              border: `1px solid ${borderColor}`,
              borderRadius: 8,
              padding: '8px',
              cursor: 'pointer',
              color: textSecondary,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ArrowLeft size={18} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: `linear-gradient(135deg, ${accent}, #e55b00)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Code2 size={20} color="#fff" />
            </div>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: textPrimary, margin: 0 }}>
                Autonomous Software Engineer
              </h1>
              <p style={{ fontSize: 13, color: textSecondary, margin: 0 }}>
                Describe any app — get a live preview in seconds
              </p>
            </div>
          </div>
        </div>

        {/* ── Main Layout ── */}
        <div style={{ display: 'grid', gridTemplateColumns: isReady ? '1fr 1.5fr' : '1fr', gap: 20 }}>

          {/* ── Left Panel: Prompt + Status + Logs ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Prompt Input */}
            <div style={{ ...card(isDark), borderRadius: 16, padding: 20 }}>

              {/* Model Selector */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                {/* Provider Toggle */}
                <div style={{
                  display: 'flex',
                  borderRadius: 8,
                  border: `1px solid ${borderColor}`,
                  overflow: 'hidden',
                }}>
                  {[
                    { key: 'anthropic', label: 'Anthropic', icon: Brain },
                    { key: 'openai', label: 'OpenAI', icon: Cpu },
                  ].map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      onClick={() => {
                        setProvider(key);
                        setModel(MODEL_OPTIONS[key][0].id);
                      }}
                      disabled={isBuilding}
                      style={{
                        padding: '6px 14px',
                        fontSize: 13,
                        fontWeight: 600,
                        border: 'none',
                        cursor: isBuilding ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        background: provider === key
                          ? accent
                          : isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                        color: provider === key ? '#fff' : textSecondary,
                        transition: 'all 0.2s',
                      }}
                    >
                      <Icon size={14} />
                      {label}
                    </button>
                  ))}
                </div>

                {/* Model Dropdown (custom, no native <select>) */}
                <div ref={dropdownRef} style={{ position: 'relative', flex: 1 }}>
                  <button
                    type="button"
                    onClick={() => !isBuilding && setModelDropdownOpen((o) => !o)}
                    disabled={isBuilding}
                    style={{
                      width: '100%',
                      padding: '7px 32px 7px 12px',
                      fontSize: 13,
                      fontWeight: 500,
                      borderRadius: 8,
                      border: `1px solid ${borderColor}`,
                      background: isDark ? '#1a2236' : '#f8fafc',
                      color: isDark ? '#e2e8f0' : '#1e293b',
                      cursor: isBuilding ? 'not-allowed' : 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {MODEL_OPTIONS[provider].find((m) => m.id === model)?.label || 'Select model'}
                  </button>
                  <ChevronDown
                    size={14}
                    color={textSecondary}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                  />
                  {modelDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        borderRadius: 8,
                        border: `1px solid ${borderColor}`,
                        background: isDark ? '#1a2236' : '#ffffff',
                        boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.12)',
                        zIndex: 50,
                        overflow: 'hidden',
                      }}
                    >
                      {MODEL_OPTIONS[provider].map((m) => (
                        <div
                          key={m.id}
                          onClick={() => { setModel(m.id); setModelDropdownOpen(false); }}
                          style={{
                            padding: '9px 14px',
                            fontSize: 13,
                            fontWeight: model === m.id ? 600 : 400,
                            color: model === m.id ? accent : (isDark ? '#e2e8f0' : '#1e293b'),
                            background: model === m.id
                              ? (isDark ? 'rgba(248,114,23,0.1)' : 'rgba(248,114,23,0.06)')
                              : 'transparent',
                            cursor: 'pointer',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => {
                            if (model !== m.id) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9';
                          }}
                          onMouseLeave={(e) => {
                            if (model !== m.id) e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          {m.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <label style={{ fontSize: 13, fontWeight: 600, color: textSecondary, marginBottom: 8, display: 'block' }}>
                Describe your application
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. Build a modern SaaS landing page with pricing cards, feature sections, testimonials, and a dark/light theme toggle..."
                disabled={isBuilding}
                rows={5}
                style={{
                  width: '100%',
                  background: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                  border: `1px solid ${borderColor}`,
                  borderRadius: 12,
                  padding: '12px 14px',
                  color: textPrimary,
                  fontSize: 14,
                  resize: 'vertical',
                  outline: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button
                  onClick={handleBuild}
                  disabled={isBuilding || !prompt.trim()}
                  style={{
                    flex: 1,
                    background: isBuilding ? `${accent}80` : accent,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    padding: '10px 20px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: isBuilding || !prompt.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    transition: 'background 0.2s',
                  }}
                >
                  {isBuilding ? (
                    <><Loader2 size={16} className="animate-spin" /> Building...</>
                  ) : (
                    <><Play size={16} /> Build & Preview</>
                  )}
                </button>
                {sessionId && (
                  <button
                    onClick={handleCleanup}
                    style={{
                      background: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
                      border: `1px solid ${borderColor}`,
                      borderRadius: 10,
                      padding: '10px 14px',
                      cursor: 'pointer',
                      color: textSecondary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    <Trash2 size={14} /> Cleanup
                  </button>
                )}
              </div>
            </div>

            {/* Pipeline Status */}
            {(isBuilding || isReady || isError) && (
              <div style={{ ...card(isDark), borderRadius: 16, padding: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: textPrimary, margin: '0 0 14px' }}>
                  Pipeline Status
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {STAGES.map((stage) => {
                    const isCompleted = completedStages.has(stage.key);
                    const isCurrent = currentStage === stage.key || currentStage?.startsWith(stage.key);
                    const Icon = stage.icon;
                    return (
                      <div
                        key={stage.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 12px',
                          borderRadius: 8,
                          background: isCurrent
                            ? isError ? 'rgba(239,68,68,0.08)' : `${accent}15`
                            : isCompleted
                            ? isDark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)'
                            : 'transparent',
                          border: isCurrent
                            ? isError ? '1px solid rgba(239,68,68,0.3)' : `1px solid ${accent}40`
                            : '1px solid transparent',
                        }}
                      >
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 7,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isCompleted
                              ? '#22c55e'
                              : isCurrent && isError
                              ? '#ef4444'
                              : isCurrent
                              ? accent
                              : isDark
                              ? 'rgba(255,255,255,0.05)'
                              : '#f1f5f9',
                          }}
                        >
                          {isCompleted ? (
                            <Check size={14} color="#fff" />
                          ) : isCurrent && isError ? (
                            <AlertCircle size={14} color="#fff" />
                          ) : isCurrent ? (
                            <Loader2 size={14} color="#fff" className="animate-spin" />
                          ) : (
                            <Icon size={14} color={textSecondary} />
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: isCurrent ? 600 : 500,
                            color: isCompleted ? '#22c55e' : isCurrent && isError ? '#ef4444' : isCurrent ? accent : textSecondary,
                          }}
                        >
                          {stage.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Error Banner */}
            {error && (
              <div
                style={{
                  ...card(isDark),
                  borderRadius: 12,
                  padding: '14px 16px',
                  border: '1px solid rgba(239,68,68,0.3)',
                  background: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.05)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                }}
              >
                <AlertCircle size={18} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 13, color: '#ef4444', lineHeight: 1.5, wordBreak: 'break-word' }}>
                  {error}
                </div>
              </div>
            )}

            {/* Deploy + URLs */}
            {isReady && (
              <div style={{ ...card(isDark), borderRadius: 16, padding: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: textPrimary, margin: '0 0 14px' }}>
                  Deployment
                </h3>

                {/* Tunnel URL */}
                {tunnelUrl && (
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: textSecondary, marginBottom: 4, display: 'block' }}>
                      Preview URL
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        background: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                        border: `1px solid ${borderColor}`,
                        borderRadius: 8,
                        padding: '8px 10px',
                      }}
                    >
                      <Globe size={14} color={accent} />
                      <a
                        href={tunnelUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ flex: 1, fontSize: 13, color: accent, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {tunnelUrl}
                      </a>
                      <button
                        onClick={() => copyUrl(tunnelUrl, 'tunnel')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: textSecondary }}
                      >
                        {copied === 'tunnel' ? <Check size={14} color="#22c55e" /> : <Copy size={14} />}
                      </button>
                      <a href={tunnelUrl} target="_blank" rel="noopener noreferrer" style={{ color: textSecondary, padding: 4 }}>
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                )}

                {/* Vercel URL */}
                {vercelUrl && (
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: textSecondary, marginBottom: 4, display: 'block' }}>
                      Production URL
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        background: isDark ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.04)',
                        border: '1px solid rgba(34,197,94,0.2)',
                        borderRadius: 8,
                        padding: '8px 10px',
                      }}
                    >
                      <Rocket size={14} color="#22c55e" />
                      <a
                        href={vercelUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ flex: 1, fontSize: 13, color: '#22c55e', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {vercelUrl}
                      </a>
                      <button
                        onClick={() => copyUrl(vercelUrl, 'vercel')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: textSecondary }}
                      >
                        {copied === 'vercel' ? <Check size={14} color="#22c55e" /> : <Copy size={14} />}
                      </button>
                      <a href={vercelUrl} target="_blank" rel="noopener noreferrer" style={{ color: textSecondary, padding: 4 }}>
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                )}

                {/* Deploy Button */}
                {status !== 'deployed' && (
                  <button
                    onClick={handleDeploy}
                    disabled={status === 'deploying' || !sessionId}
                    style={{
                      width: '100%',
                      background: status === 'deploying' ? '#22c55e80' : '#22c55e',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      padding: '10px 20px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: status === 'deploying' ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      marginTop: 4,
                    }}
                  >
                    {status === 'deploying' ? (
                      <><Loader2 size={16} className="animate-spin" /> Deploying to Vercel...</>
                    ) : (
                      <><Rocket size={16} /> Deploy to Vercel</>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Conversation & Edit Input (when session is active) */}
            {sessionId && isReady && (
              <div style={{ ...card(isDark), borderRadius: 16, padding: 0, overflow: 'hidden' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderBottom: `1px solid ${borderColor}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <MessageSquare size={14} color={textSecondary} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: textPrimary }}>Conversation</span>
                  </div>
                  <span style={{ fontSize: 11, color: textSecondary }}>
                    {messages.length} message{messages.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Messages */}
                {messages.length > 0 && (
                  <div style={{ maxHeight: 250, overflow: 'auto', padding: '12px 16px' }}>
                    {messages.map((msg, i) => (
                      <div
                        key={i}
                        style={{
                          padding: '8px 12px',
                          borderRadius: 10,
                          marginBottom: 8,
                          maxWidth: '85%',
                          marginLeft: msg.role === 'user' ? 'auto' : 0,
                          marginRight: msg.role === 'assistant' ? 'auto' : 0,
                          background: msg.role === 'user'
                            ? isDark ? `${accent}20` : `${accent}10`
                            : isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
                          border: msg.role === 'user'
                            ? `1px solid ${accent}30`
                            : `1px solid ${borderColor}`,
                        }}
                      >
                        <div style={{ fontSize: 10, fontWeight: 600, color: msg.role === 'user' ? accent : textSecondary, marginBottom: 2 }}>
                          {msg.role === 'user' ? 'You' : 'Engineer'}
                        </div>
                        <div style={{
                          fontSize: 13,
                          color: textPrimary,
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}>
                          {msg.content}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Edit input */}
                <div
                  style={{
                    padding: '12px 16px',
                    borderTop: messages.length > 0 ? `1px solid ${borderColor}` : 'none',
                    display: 'flex',
                    gap: 8,
                  }}
                >
                  <input
                    type="text"
                    value={editPrompt}
                    onChange={(e) => setEditPrompt(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEdit(); } }}
                    placeholder="Describe changes to make..."
                    disabled={isEditing}
                    style={{
                      flex: 1,
                      background: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                      border: `1px solid ${borderColor}`,
                      borderRadius: 10,
                      padding: '10px 14px',
                      color: textPrimary,
                      fontSize: 13,
                      outline: 'none',
                      fontFamily: 'inherit',
                    }}
                  />
                  <button
                    onClick={handleEdit}
                    disabled={isEditing || !editPrompt.trim()}
                    style={{
                      background: isEditing ? `${accent}80` : accent,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      padding: '10px 16px',
                      cursor: isEditing || !editPrompt.trim() ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {isEditing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    {isEditing ? 'Applying...' : 'Apply'}
                  </button>
                </div>
              </div>
            )}

            {/* Logs */}
            {logs.length > 0 && (
              <div style={{ ...card(isDark), borderRadius: 16, padding: 0, overflow: 'hidden' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderBottom: `1px solid ${borderColor}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Terminal size={14} color={textSecondary} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: textPrimary }}>Build Logs</span>
                  </div>
                  <span style={{ fontSize: 11, color: textSecondary }}>{logs.length} lines</span>
                </div>
                <div
                  style={{
                    maxHeight: 300,
                    overflow: 'auto',
                    padding: '12px 16px',
                    fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
                    fontSize: 12,
                    lineHeight: 1.7,
                    background: isDark ? '#0a0f1e' : '#f9fafb',
                  }}
                >
                  {logs.map((log, i) => (
                    <div key={i} style={{ color: log.text.includes('✓') ? '#22c55e' : log.text.includes('✗') || log.text.toLowerCase().includes('error') ? '#ef4444' : textSecondary }}>
                      {log.text}
                    </div>
                  ))}
                  <div ref={logsEndRef} />
                </div>
              </div>
            )}
          </div>

          {/* ── Right Panel: Live Preview ── */}
          {isReady && tunnelUrl && (
            <div style={{ ...card(isDark), borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Browser chrome */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 16px',
                  borderBottom: `1px solid ${borderColor}`,
                  background: isDark ? '#0a0f1e' : '#f9fafb',
                }}
              >
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444' }} />
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#eab308' }} />
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e' }} />
                </div>
                <div
                  style={{
                    flex: 1,
                    background: isDark ? 'rgba(255,255,255,0.05)' : '#ffffff',
                    border: `1px solid ${borderColor}`,
                    borderRadius: 6,
                    padding: '5px 10px',
                    fontSize: 12,
                    color: textSecondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tunnelUrl}
                </div>
                <button
                  onClick={() => setIframeKey((k) => k + 1)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    color: textSecondary,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  title="Reload preview"
                >
                  <RefreshCw size={14} />
                </button>
              </div>
              {/* iframe */}
              <iframe
                key={iframeKey}
                src={tunnelUrl}
                title="Live Preview"
                style={{
                  flex: 1,
                  minHeight: 'calc(100vh - 200px)',
                  border: 'none',
                  background: '#ffffff',
                }}
                sandbox="allow-scripts allow-forms allow-popups allow-presentation"
              />
            </div>
          )}

          {/* ── Empty State (before build or no preview) ── */}
          {!isReady && !isBuilding && !isError && (
            <div style={{ display: 'none' }} /> // Grid collapses to single column
          )}
          {isBuilding && (
            <div
              style={{
                ...card(isDark),
                borderRadius: 16,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 400,
                gap: 16,
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 16,
                  background: `linear-gradient(135deg, ${accent}20, ${accent}05)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Loader2 size={28} color={accent} className="animate-spin" />
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 16, fontWeight: 600, color: textPrimary, margin: '0 0 4px' }}>
                  Building your application...
                </p>
                <p style={{ fontSize: 13, color: textSecondary, margin: 0 }}>
                  Generating code, setting up the environment
                </p>
              </div>
            </div>
          )}
          {isError && (
            <div
              style={{
                ...card(isDark),
                borderRadius: 16,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 400,
                gap: 16,
                border: '1px solid rgba(239,68,68,0.2)',
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 16,
                  background: isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AlertCircle size={28} color="#ef4444" />
              </div>
              <div style={{ textAlign: 'center', maxWidth: 500, padding: '0 20px' }}>
                <p style={{ fontSize: 16, fontWeight: 600, color: '#ef4444', margin: '0 0 8px' }}>
                  Build Failed
                </p>
                <p style={{ fontSize: 13, color: textSecondary, margin: '0 0 16px', lineHeight: 1.6, wordBreak: 'break-word' }}>
                  {error}
                </p>
                <button
                  onClick={() => { setStatus('idle'); setError(null); setCurrentStage(null); setCompletedStages(new Set()); setLogs([]); }}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 8,
                    background: accent,
                    color: '#fff',
                    border: 'none',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <RefreshCw size={14} /> Try Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div> {/* end main content */}
      </div> {/* end flex wrapper */}
    </div>
  );
}
